import React, { useEffect, useState, useRef } from 'react';
import { LanguageSelector } from './components/LanguageSelector';
import { SlideOne } from './components/SlideOne';
import { SlideTwo } from './components/SlideTwo';
import { SlideThree } from './components/SlideThree';
import { SlideFour } from './components/SlideFour';
import { SlideFive } from './components/SlideFive';
import { ProgressIndicator } from './components/ProgressIndicator';
import { CountdownTimer } from './components/CountdownTimer';
import { Footer } from './components/Footer';
import { Storyboard } from './components/Storyboard';
import { ReviewDocument } from './components/ReviewDocument';
import { I18nProvider, Lang } from './i18n';
export function App() {
  const [showStoryboard, setShowStoryboard] = useState<boolean>(false);
  const [showReviewDoc, setShowReviewDoc] = useState<boolean>(false);
  // Slide 0 = language, 1 = poll, 2 = results, 3 = Nils, 4 = CTA options, 5 = form
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Lang | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [selectedCTA, setSelectedCTA] = useState<string | null>(null);
  // Timer state
  const [elapsed, setElapsed] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [finalElapsed, setFinalElapsed] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Start/stop timer
  useEffect(() => {
    if (timerRunning) {
      intervalRef.current = setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerRunning]);
  const transition = (callback: () => void) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      callback();
      setIsTransitioning(false);
    }, 300);
  };
  const goTo = (slide: number) => {
    // Stop timer when reaching CTA page (slide 4)
    if (slide === 4 && finalElapsed === null) {
      setTimerRunning(false);
      setFinalElapsed(elapsed);
    }
    transition(() => setCurrentSlide(slide));
  };
  const handleLanguageSelect = (lang: string) => {
    setSelectedLanguage(lang as Lang);
    setTimerRunning(true);
    goTo(1);
  };
  const handlePollNext = (option: number) => {
    setSelectedOption(option);
    goTo(2);
  };
  const handleBack = () => {
    if (currentSlide <= 0) return;
    if (currentSlide === 4) {
      goTo(3);
    } else if (currentSlide === 5) {
      goTo(4);
    } else {
      transition(() => setCurrentSlide(prev => prev - 1));
    }
  };
  const handleGoToCTA = () => goTo(4);
  const handleSelectCTA = (option: string) => {
    setSelectedCTA(option);
    goTo(5);
  };
  const handleNextSlide = () => {
    if (currentSlide < 3) {
      transition(() => setCurrentSlide(prev => prev + 1));
    }
  };
  const handleChangeLanguage = () => {
    setTimerRunning(false);
    setElapsed(0);
    setFinalElapsed(null);
    setSelectedOption(null);
    setSelectedCTA(null);
    transition(() => setCurrentSlide(0));
  };
  const LANG_FLAGS: Record<string, string> = {
    en: '🇬🇧',
    no: '🇳🇴',
    sv: '🇸🇪',
    da: '🇩🇰'
  };
  // Show progress dots only for content slides 1-3
  const showProgress = currentSlide >= 1 && currentSlide <= 3;
  const showRunningTimer = currentSlide >= 1 && currentSlide <= 3;
  const showLangButton = currentSlide >= 1;
  if (showStoryboard) {
    return <Storyboard onClose={() => setShowStoryboard(false)} data-id="element-0" />;
  }
  if (showReviewDoc) {
    return <ReviewDocument onClose={() => setShowReviewDoc(false)} data-id="element-1" />;
  }
  const appContent = <I18nProvider lang={selectedLanguage || 'en'} data-id="element-2">
      <div className="min-h-full w-full bg-solventum-bg flex flex-col items-center justify-between px-6 py-4 overflow-hidden selection:bg-solventum-primary selection:text-solventum-bg" data-id="element-3">
        {showLangButton && <div className="w-full max-w-lg flex items-center justify-between pt-1 pb-2" data-id="element-4">
            <button onClick={handleChangeLanguage} className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors duration-200 text-xs font-body group" aria-label="Change language" data-id="element-5">
              <span className="text-base" data-id="element-6">
                {LANG_FLAGS[selectedLanguage || 'en']}
              </span>
              <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-7">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" data-id="element-8" />
              </svg>
            </button>
            {showRunningTimer ? <CountdownTimer elapsed={elapsed} data-id="element-9" /> : <div data-id="element-10" />}
          </div>}

        <main className="flex-1 w-full max-w-lg flex flex-col justify-center relative" data-id="element-11">
          <div className={`transition-opacity duration-300 w-full h-full flex items-center ${isTransitioning ? 'opacity-0' : 'opacity-100'}`} data-id="element-12">
            {currentSlide === 0 && <LanguageSelector onSelect={handleLanguageSelect} data-id="element-13" />}
            {currentSlide === 1 && <SlideOne onNext={handlePollNext} onCTA={handleGoToCTA} data-id="element-14" />}
            {currentSlide === 2 && <SlideTwo onNext={handleNextSlide} onBack={handleBack} onCTA={handleGoToCTA} selectedOption={selectedOption} data-id="element-15" />}
            {currentSlide === 3 && <SlideThree selectedOption={selectedOption} onBack={handleBack} onCTA={handleGoToCTA} data-id="element-16" />}
            {currentSlide === 4 && <SlideFour onSelectCTA={handleSelectCTA} onBack={handleBack} selectedOption={selectedOption} elapsedSeconds={finalElapsed ?? elapsed} data-id="element-17" />}
            {currentSlide === 5 && <SlideFive ctaOption={selectedCTA} onBack={handleBack} data-id="element-18" />}
          </div>
        </main>

        <div className="w-full max-w-lg flex flex-col items-center mt-4" data-id="element-19">
          {showProgress && <ProgressIndicator total={3} current={currentSlide} data-id="element-20" />}
          <Footer data-id="element-21" />
        </div>
      </div>
    </I18nProvider>;
  return <div className="min-h-screen w-full bg-gray-200 flex flex-col items-center justify-center py-8 px-4" data-id="element-22">
      {/* Phone frame */}
      <div className="w-[390px] h-[844px] bg-solventum-bg rounded-[3rem] border-[10px] border-gray-800 overflow-hidden relative shadow-2xl flex flex-col" data-id="element-23">
        {/* Notch */}
        <div className="absolute top-0 inset-x-0 h-7 bg-solventum-bg z-30 flex justify-center" data-id="element-24">
          <div className="w-28 h-5 bg-gray-800 rounded-b-2xl" data-id="element-25" />
        </div>
        {/* Home indicator */}
        <div className="absolute bottom-1.5 inset-x-0 z-30 flex justify-center" data-id="element-26">
          <div className="w-32 h-1 bg-white/20 rounded-full" data-id="element-27" />
        </div>
        {/* App content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden mt-5 pb-4" data-id="element-28">
          {appContent}
        </div>
      </div>

      {/* Links below phone */}
      <div className="flex gap-4 mt-6" data-id="element-29">
        <button onClick={() => setShowStoryboard(true)} className="text-gray-500 hover:text-gray-800 text-xs font-body tracking-wide transition-colors" data-id="element-30">
          View Storyboard
        </button>
        <span className="text-gray-400" data-id="element-31">·</span>
        <button onClick={() => setShowReviewDoc(true)} className="text-gray-500 hover:text-gray-800 text-xs font-body tracking-wide transition-colors" data-id="element-32">
          Review Document
        </button>
      </div>
    </div>;
}